import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface IEnrichmentCreditBalance {
  balance: number;
}

/** Wire shape of com.kyloe.datarules.enrichment.ledger.EnrichmentTransaction. */
export interface IEnrichmentTransaction {
  date: number; // epoch millis
  totalAmount: number;
  balance: number;
  details: string;
  ruleId: string | null;
  ruleName: string | null;
  userId: string | null;
  userName: string | null;
  creditCombinations: Record<string, number> | null;
}

export const ENRICH_CREDIT_EXHAUSTED_TOOLTIP = 'Kyloe credit balance exhausted. Enrichment cannot run until credits are renewed. Contact your Account Manager to discuss adding additional credits';

export const ENRICH_CREDIT_ICON_FULL = '/images/credit_E_full.svg';
export const ENRICH_CREDIT_ICON_EMPTY = '/images/credit_E_empty.svg';


/** Credits left on the corporation's balance. Never negative, even if the balance is somehow reported below zero. */
export function enrichmentCreditsRemaining(balance: IEnrichmentCreditBalance): number {
  return Math.max(0, balance.balance);
}

/** Sentence fragment for tooltips, e.g. '1 Kyloe credit remaining' or 'no Kyloe credits remaining'. */
export function enrichmentCreditsRemainingText(remaining: number): string {
  if (remaining <= 0) {
    return 'no Kyloe credits remaining';
  }
  return `${remaining.toLocaleString()} Kyloe credit${remaining === 1 ? '' : 's'} remaining`;
}

export function enrichmentCreditIcon(remaining: number): string {
  return remaining > 0 ? ENRICH_CREDIT_ICON_FULL : ENRICH_CREDIT_ICON_EMPTY;
}

/** Tooltip shared by the credit pills in the app header and the Enrichment action. */
export function enrichmentCreditPillTooltip(remaining: number): string {
  return `You have ${enrichmentCreditsRemainingText(remaining)}`;
}

@Injectable({ providedIn: 'root' })
export class EnrichmentCreditService {

  constructor(private http: HttpClient) {}

  getCreditBalance(): Observable<IEnrichmentCreditBalance> {
    return this.http.get<number>(`${environment.dataRulesUrl}/enrichment/credits/balance`).pipe(
      map(balance => ({ balance })),
      catchError(error => {
        console.error('Could not get Kyloe credit balance, defaulted to 0.', error);
        return of({ balance: 0 });
      })
    );
  }

  getTransactions(): Observable<IEnrichmentTransaction[]> {
    return this.http.get<IEnrichmentTransaction[]>(`${environment.dataRulesUrl}/enrichment/credits/transactions`).pipe(
      catchError(error => {
        console.error('Could not get Kyloe credit transaction history, defaulted to an empty list.', error);
        return of([]);
      })
    );
  }
}
